import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { GlassFilter } from './LiquidGlassCard';

const BUTTON_GLASS_FILTER_SCALE = 70;

// Liquid Button - extends shadcn Button with glossy glass effect
const liquidButtonVariants = cva(
    'relative transition-all duration-300 overflow-hidden backdrop-blur-xl',
    {
        variants: {
            liquidVariant: {
                default: 'hover:scale-[1.02] active:scale-[0.98]',
                none: '',
            },
        },
        defaultVariants: {
            liquidVariant: 'default',
        },
    }
);

export type LiquidButtonProps = ButtonProps &
    VariantProps<typeof liquidButtonVariants> & {
        liquidVariant?: 'default' | 'none';
    };

function LiquidButton({
    className,
    liquidVariant = 'default',
    children,
    ...props
}: LiquidButtonProps) {
    const filterId = React.useId();

    return (
        <>
            <Button
                className={cn(liquidButtonVariants({ liquidVariant }), className)}
                {...props}
            >
                {/* Glossy glass background with gradient */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/20 via-white/5 to-transparent rounded-[inherit]" />
                
                {/* Top highlight for 3D effect */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent rounded-t-[inherit] opacity-60" />
                
                {/* Bottom shadow for depth */}
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/20 to-transparent rounded-b-[inherit]" />
                
                {/* Outer glow */}
                <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[0_8px_32px_rgba(0,0,0,0.4),inset_0_1px_1px_rgba(255,255,255,0.2),inset_0_-1px_1px_rgba(0,0,0,0.2)]" />
                
                {/* Liquid distortion filter */}
                <div
                    className="pointer-events-none absolute inset-0 isolate -z-10 overflow-hidden rounded-[inherit]"
                    style={{ backdropFilter: `url("#${filterId}")` }}
                />
                
                {/* Border highlight */}
                <div className="pointer-events-none absolute inset-0 rounded-[inherit] ring-1 ring-inset ring-white/10" />
                
                <span className="relative z-10">{children}</span>
            </Button>
            <GlassFilter id={filterId} scale={BUTTON_GLASS_FILTER_SCALE} />
        </>
    );
}

export { LiquidButton };
